/** Apply a reviewed assistant export proposal to the Export tab without
 * starting an export. The user retains the final Preview/Export click. */

import type { ExportSpec } from '../../api/types.ts';
import {
  DEFAULT_OUTLIERS,
  derivedSignals,
  endDate,
  exportFormat,
  filterExpr,
  filterExprError,
  filterLogic,
  filterMode,
  fillMethod,
  filters,
  frequencyUnit,
  frequencyValue,
  outliers,
  startDate,
  activeDataSource,
  selectedCols,
} from '../../state/store.ts';
import { restoreFromUrl } from '../../state/url-sync.ts';
import { assertValidProposalDates } from './proposal-dates.ts';
import { ensureHistoricalProposalSource } from './proposal-source.ts';
import { revealInExplore } from './apply-plot.ts';

export function exportSpecToQuery(spec: ExportSpec): string {
  const resolvedStart = spec.start_date || startDate.value;
  const resolvedEnd = spec.end_date || endDate.value;
  assertValidProposalDates(resolvedStart, resolvedEnd);
  const p = new URLSearchParams();
  p.set('tab', 'export');
  p.set('ds', 'historical');
  p.set('ship', spec.ship_id);
  p.set('start', resolvedStart);
  p.set('end', resolvedEnd);
  if (spec.columns.length) p.set('sel', spec.columns.join(','));
  if (spec.format !== 'csv') p.set('format', spec.format);
  if (spec.frequency) p.set('freq', spec.frequency);
  if (spec.fill_method !== 'none') p.set('fill', spec.fill_method);
  if (spec.filter_expr) {
    p.set('fexpr', spec.filter_expr);
    p.set('fmode', 'and');
  }
  return p.toString();
}

/** Clear every state surface not represented by ExportSpec before URL restore. */
export function resetExportProposalState(spec: ExportSpec): void {
  activeDataSource.set('historical');
  selectedCols.set(new Set());
  filters.set([]);
  filterLogic.set('AND');
  outliers.set({ ...DEFAULT_OUTLIERS, columns: [] });
  derivedSignals.set([]);
  exportFormat.set('csv');
  fillMethod.set('none');
  frequencyValue.set('');
  frequencyUnit.set('min');
  filterExpr.set('');
  filterExprError.set(null);
  filterMode.set('and');
}

export async function applyProposedExport(spec: ExportSpec): Promise<void> {
  const query = exportSpecToQuery(spec);
  await ensureHistoricalProposalSource();
  resetExportProposalState(spec);
  revealInExplore('export');
  await restoreFromUrl('?' + query);
}
